
import React, { useEffect, useState } from "react";
import axios from "axios";
import "./DocumentUploadScreen.css";
import CamIcon from "./imagesicons/camera.png";
import UploadIcon from "./imagesicons/upload.png";
import { useNavigate, useParams,useLocation } from "react-router-dom";
import arrow_icon from "./imagesicons/arrow_back.png";
import rejectIcon from "./imagesicons/Rejected.png";
import { jsPDF } from "jspdf";

const DocumentUploadScreen = () => {
  const navigate = useNavigate();
  const { audit_id } = useParams();
const location = useLocation();

  /* ---------------- STATE ---------------- */
  const [documents, setDocuments] = useState([
    { name: "Claim Form", required: true, file: null, uploaded: false, previewUrl: null, s3Path: null },
    { name: "Death Certificate", required: true, file: null, uploaded: false, previewUrl: null, s3Path: null },
    { name: "Postmortem Report", required: true, file: null, uploaded: false, previewUrl: null, s3Path: null },
    { name: "Pan Copy / Cancelled Cheque", required: true, file: null, uploaded: false, previewUrl: null, s3Path: null },
    { name: "Panchanama / FIR", required: true, file: null, uploaded: false, previewUrl: null, s3Path: null },
  ]);

  /* ---------------- DERIVED VALUES ---------------- */
  const totalRequired = documents.filter((d) => d.required).length;
  const uploadedRequired = documents.filter((d) => d.required && (d.file || d.s3Path)).length;
  const isSubmitDisabled = uploadedRequired !== totalRequired;
useEffect(() => {
  const loadExisting = async () => {
    try {
      const res = await axios.get(`/process_data/documents/list?audit_id=${encodeURIComponent(audit_id)}`);
      const existing = res.data?.documents || [];

      // const norm = (s = "") => s.toLowerCase().replace(/\s+/g, " ").trim();
        setDocuments((prev) =>
          prev.map((doc) => {
            const key = sanitize(doc.name); // "Pan_Copy_Cancelled_Cheque"
            const match = existing.find((x) => x.docKey === key);
            return match ? { ...doc, uploaded: true, s3Path: match.s3Path } : doc;
          })
        );
   
    } catch (e) {
      console.error("Failed to load existing docs", e);
    }
  };

  loadExisting();
}, [audit_id]);
  /* ---------------- CLEANUP: REVOKE OBJECT URLS ---------------- */
  useEffect(() => {
    return () => {
      documents.forEach((d) => {
        if (d.previewUrl) URL.revokeObjectURL(d.previewUrl);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------------- API: GET SIGNED DOWNLOAD URL ---------------- */
  const fetchSignedPreviewUrl = async (s3Path) => {
    const res = await axios.get(`/process_data/preview_url?key=${encodeURIComponent(s3Path)}`);
    return res.data.url;
  };

  /* ---------------- HELPERS ---------------- */
  const sanitize = (s = "") =>
    s.toString().trim().replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "");

  const safePdfFileName = (docName) => `${sanitize(docName)}.pdf`;

  // Convert captured image -> single-page PDF File
  const imageFileToPdfFile = async (imageFile, pdfName) => {
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(imageFile);
    });

    const imgEl = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = dataUrl;
    });

    const pdf = new jsPDF({
      orientation: imgEl.width >= imgEl.height ? "l" : "p",
      unit: "px",
      format: [imgEl.width, imgEl.height],
      compress: true,
    });

    pdf.addImage(dataUrl, "JPEG", 0, 0, imgEl.width, imgEl.height);

    const pdfBlob = pdf.output("blob");
    return new File([pdfBlob], pdfName, { type: "application/pdf" });
  };

  /* ---------------- HANDLERS ---------------- */
  const handleFileChange = async (e, index, source) => {
    const picked = e.target.files?.[0];
    if (!picked) return;

    // reset input so user can open picker again for other rows
    e.target.value = "";

    // lock check (no reupload/capture once)
    const current = documents[index];
    if (current?.file || current?.uploaded || current?.s3Path) return;

    try {
      let pdfFile = null;

      if (source === "camera") {
        if (!picked.type.startsWith("image/")) {
          alert("Camera capture must be an image. Please try again.");
          return;
        }
        pdfFile = await imageFileToPdfFile(picked, safePdfFileName(documents[index].name));
      } else {
        const isPdf =
          picked.type === "application/pdf" || picked.name.toLowerCase().endsWith(".pdf");
        if (!isPdf) {
          alert("Only PDF files are allowed for upload.");
          return;
        }
        pdfFile = new File([picked], safePdfFileName(documents[index].name), { type: "application/pdf" });
      }

      const previewUrl = URL.createObjectURL(pdfFile);

      setDocuments((prev) =>
        prev.map((doc, i) => {
          if (i !== index) return doc;

          if (doc.file || doc.uploaded || doc.s3Path) {
            URL.revokeObjectURL(previewUrl);
            return doc;
          }

          if (doc.previewUrl) URL.revokeObjectURL(doc.previewUrl);

          return { ...doc, file: pdfFile, previewUrl };
        })
      );

      // Auto-save the file immediately to the backend
      try {
        const formData = new FormData();
        formData.append("files", pdfFile);
        formData.append("file_names", documents[index].name);
        formData.append("audit_id", audit_id);

        const response = await axios.post("/process_data/upload_documents", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        const uploadedFiles = response?.data?.files || [];
        const match = uploadedFiles.find((x) => x.originalName === pdfFile.name);

        if (match) {
          setDocuments((prev) =>
            prev.map((doc, i) => {
              if (i !== index) return doc;
              return {
                ...doc,
                uploaded: true,
                s3Path: match.s3Path,
                file: null, // Clear local file since it's saved on the server
              };
            })
          );
        }
      } catch (uploadErr) {
        console.error("Auto-save failed:", uploadErr);
      }
    } catch (err) {
      console.error("Failed to process file:", err);
      alert("Failed to process the file. Please try again.");
    }
  };

  // ✅ "Preview" downloads PDF (no UI modal)
  const openPreview = async (doc) => {
    try {
      let fileUrl = null;

      if (doc.previewUrl) {
        fileUrl = doc.previewUrl;
      } else if (doc.s3Path) {
        fileUrl = await fetchSignedPreviewUrl(doc.s3Path);
      }

      if (!fileUrl) {
        alert("No file available to download.");
        return;
      }
      window.open(fileUrl, "_blank", "noopener,noreferrer");
      // const fileName = downloadName(doc.name);

      // const link = document.createElement("a");
      // link.href = fileUrl;
      // link.download = fileName;
      // document.body.appendChild(link);
      // link.click();
      // document.body.removeChild(link);
    } catch (err) {
      console.error("Download error:", err);
      alert("Failed to download file.");
    }
  };

  // const handleSubmit = async () => {
  //   try {
  //     const formData = new FormData();

  //     documents.forEach((doc) => {
  //       if (doc.file) {
  //         formData.append("files", doc.file);       // always PDF
  //         formData.append("file_names", doc.name);  // backend mapping
  //       }
  //     });

  //     formData.append("audit_id", audit_id);

  //     const response = await axios.post("/process_data/upload_documents", formData, {
  //       headers: { "Content-Type": "multipart/form-data" },
  //     });

  //     const uploadedFiles = response?.data?.files || [];

  //     setDocuments((prev) =>
  //       prev.map((doc) => {
  //         if (!doc.file) return doc;

  //         const match = uploadedFiles.find((x) => x.originalName === doc.file?.name);

  //         return {
  //           ...doc,
  //           uploaded: true,
  //           s3Path: match?.s3Path || doc.s3Path,
  //         };
  //       })
  //     );

  //     navigate("/auditList", { state: { activeTab: "claim" } });
  //   } catch (err) {
  //     console.error("Upload error:", err);
  //     alert("Upload failed. Please try again.");
  //   }
  // };
const handleSubmit = async () => {
  try {
    // Check if there are new files to upload
    const hasNewFiles = documents.some((doc) => doc.file);

    if (!hasNewFiles) {
      // ✅ Already uploaded previously → just navigate
      navigate("/auditList", { state: { activeTab: "claim" } });
      return;
    }

    // Otherwise upload new files
    const formData = new FormData();

    documents.forEach((doc) => {
      if (doc.file) {
        formData.append("files", doc.file);
        formData.append("file_names", doc.name);
      }
    });

    formData.append("audit_id", audit_id);

    const response = await axios.post("/process_data/upload_documents", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    const uploadedFiles = response?.data?.files || [];

    setDocuments((prev) =>
      prev.map((doc) => {
        if (!doc.file) return doc;

        const match = uploadedFiles.find(
          (x) => x.originalName === doc.file?.name
        );

        return {
          ...doc,
          uploaded: true,
          s3Path: match?.s3Path || doc.s3Path,
          file: null, // clear local file after upload
        };
      })
    );

    navigate("/auditList", { state: { activeTab: "claim" } });

  } catch (err) {
    console.error("Upload error:", err);
    alert("Upload failed. Please try again.");
  }
};
  /* ---------------- COMPONENTS ---------------- */
  const Stepper = ({ completed, total }) => (
    <div className="stepper-container">
      {[...Array(total)].map((_, i) => (
        <div key={i} className={`step ${i < completed ? "step-complete" : ""}`} />
      ))}
    </div>
  );

  const DocumentItem = ({ doc, index }) => {
    const locked = !!doc.file || !!doc.uploaded || !!doc.s3Path;
    const canPreview = !!doc.previewUrl || !!doc.s3Path;

    return (
      <div className="document-item">
        <div className="document-text">
          {!locked && (
            <img
              src={rejectIcon}
              alt="Pending"
              width={20}
              height={20}
              style={{ marginRight: "6px" }}
            />
          )}

          <span className="document-name">
            {doc.name}
            {!doc.required && <span className="optional-text"> (optional)</span>}
          </span>
        </div>

        {!locked && (
          <div className="upload-buttons-container">
            {/* ✅ CHANGE: Capture clicks the hidden camera input directly */}
            <button
              className="action-button capture-button"
              onClick={() => document.getElementById(`camera-${index}`)?.click()}
            >
              Capture{" "}
              <img src={CamIcon} alt="Capture" width={10} height={10} style={{ margin: "6px" }} />
            </button>

            {/* ✅ CHANGE: Upload clicks the hidden file input directly */}
            <button
              className="action-button upload-button"
              onClick={() => document.getElementById(`file-${index}`)?.click()}
            >
              Upload{" "}
              <img src={UploadIcon} alt="Upload" width={10} height={10} style={{ margin: "6px" }} />
            </button>

            {/* Hidden inputs */}
            <input
              type="file"
              accept="image/*"
              capture="environment"
              id={`camera-${index}`}
              style={{ display: "none" }}
              onChange={(e) => handleFileChange(e, index, "camera")}
            />

            <input
              type="file"
              accept="application/pdf"
              id={`file-${index}`}
              style={{ display: "none" }}
              onChange={(e) => handleFileChange(e, index, "upload")}
            />
          </div>
        )}

        {locked && canPreview && (
          <div className="upload-buttons-container">
            <button className="action-button preview-button" onClick={() => openPreview(doc)}>
              Preview
            </button>
          </div>
        )}
      </div>
    );
  };

  /* ---------------- RENDER ---------------- */
  return (
    <div className="app-container">
      <header className="app-header">
        <img
          src={arrow_icon}
          style={{ width: "20px", height: "20px", cursor: "pointer" }}
          alt="Back"
          onClick={() => navigate(`/AuditDeathDesc/${audit_id}`, { state: { userId: location.state?.userId } })}
        />
        <span className="header-title">Documents</span>
        <div />
      </header>

      <Stepper completed={uploadedRequired} total={totalRequired} />

      <main className="document-list-scrollable">
        {documents.map((doc, index) => (
          <DocumentItem key={index} doc={doc} index={index} />
        ))}
      </main>

      {/* <footer className="app-footer">
        <button className="submit-button" disabled={isSubmitDisabled} onClick={handleSubmit}>
          Submit to Insurance Company
        </button>
      </footer> */}
      <footer className="app-footer">
  <button
    className="submit-button"
    disabled={isSubmitDisabled}
    onClick={handleSubmit}
  >
    {documents.some((doc) => doc.file)
      ? "Submit to Insurance Company"
      : "Continue"}
  </button>
</footer>
    </div>
  );
};

export default DocumentUploadScreen;
